import React, { useContext, useMemo, useState } from "react";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";

import type { StreamSettingsResponseStream } from "..";
import { faChevronRight, faExclamationCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { BillingIdentity, showFeatureModal } from "~/billing";
import { AppContext } from "~/common";
import {
	type ActionItemIntegration,
	type BaseIntegrationDetails,
	getIntegrationForKey,
	getIntegrationStreamSettingsCards,
} from "~/integrations/actionItemIntegration";
import { promptConnectingIntegrationModal } from "~/integrations/actionItemIntegration/promptConnectingIntegration";
import { asanaKey } from "~/integrations/asana";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Button } from "~/ui";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import toasty from "~/ui/toast";

import type {
	ActionItemIntegrationsConnectStreamMutation,
	ActionItemIntegrationsConnectStreamMutation$variables,
} from "~/__generated__/ActionItemIntegrationsConnectStreamMutation.graphql";
import type { ActionItemIntegrationsDisconnectStreamMutation } from "~/__generated__/ActionItemIntegrationsDisconnectStreamMutation.graphql";
import type {
	ActionItemIntegrationsStreamIntegration$data as ActionItemIntegrationsStreamIntegration,
	ActionItemIntegrationsStreamIntegration$key,
} from "~/__generated__/ActionItemIntegrationsStreamIntegration.graphql";
import type { ActionItemIntegrationsStreamSettingsModalQuery } from "~/__generated__/ActionItemIntegrationsStreamSettingsModalQuery.graphql";

import styles from "./ActionItemIntegration.module.css";

export type IntegrationDetailsType = NonNullable<ActionItemIntegrationsStreamIntegration>;

export const streamIntegrationFragment = graphql`
	fragment ActionItemIntegrationsStreamIntegration on NoteStream {
		actionItemIntegration {
			id
			integrationProvider
			integrationSpecificDetails {
				...clickupStreamDetails
				...trelloStreamDetails
				...mondayStreamDetails
				...linearStreamDetails
			}
		}
		asanaStream {
			...asanaStreamDetails
		}
	}
`;

const connectStreamToIntegration = graphql`
	mutation ActionItemIntegrationsConnectStreamMutation(
		$integrationProvider: IntegrationProvider!
		$destinationInfo: JSON!
		$streamId: String!
	) {
		connectStreamToIntegration(
			input: { integrationProvider: $integrationProvider, destinationInfo: $destinationInfo, streamId: $streamId }
		) {
			stream {
				id
				...ActionItemIntegrationsStreamIntegration
			}
		}
	}
`;

const disconnectStreamFromIntegration = graphql`
	mutation ActionItemIntegrationsDisconnectStreamMutation($streamId: ID!) {
		disconnectStreamFromIntegration(input: { streamId: $streamId }) {
			deletedStreamIntegrationId @deleteRecord
		}
	}
`;

export function connectStreamIntegration(
	integrationProvider: ActionItemIntegrationsConnectStreamMutation$variables["integrationProvider"],
	destinationInfo: ActionItemIntegrationsConnectStreamMutation$variables["destinationInfo"],
	streamId: ActionItemIntegrationsConnectStreamMutation$variables["streamId"],
) {
	return commitMutationPromise<ActionItemIntegrationsConnectStreamMutation>(environment, {
		mutation: connectStreamToIntegration,
		variables: {
			integrationProvider,
			destinationInfo,
			streamId,
		},
	});
}

export function disconnectStreamIntegration(streamId: string) {
	return commitMutationPromise<ActionItemIntegrationsDisconnectStreamMutation>(environment, {
		mutation: disconnectStreamFromIntegration,
		variables: { streamId: streamId },
	});
}

function withIntegrationDetails<T>(
	Component: React.ComponentType<T>,
	integration: ActionItemIntegration,
	integrationData: ActionItemIntegrationsStreamIntegration,
) {
	return function WithIntegrationDetails(props: T) {
		const integrationDetails = integration.useIntegrationDetails(integrationData);
		return <Component {...props} integrationDetails={integrationDetails} />;
	};
}

interface ActionItemIntegrationProps {
	stream: NonNullable<StreamSettingsResponseStream>;
}

export interface ActionItemIntegrationCardProps {
	viewer: ActionItemIntegrationsStreamSettingsModalQuery["response"]["viewer"];
	onClickIntegration: (integration: ActionItemIntegration) => void;
}

export function ActionItemIntegrations({ stream }: ActionItemIntegrationProps) {
	const data = useLazyLoadQuery<ActionItemIntegrationsStreamSettingsModalQuery>(
		graphql`
			query ActionItemIntegrationsStreamSettingsModalQuery($streamId: ID!) {
				viewer {
					...BillingIdentityFeaturesPaywall_viewer
					...AsanaIntegrationAddProjectContent_viewer
					integrations {
						asana {
							id
							isConnected
						}
					}
					connectedActionItemIntegrations
				}
				stream: node(id: $streamId) {
					...ActionItemIntegrationsStreamIntegration
				}
			}
		`,
		{ streamId: stream.id },
		{ fetchPolicy: "store-and-network" },
	);
	const { user } = useContext(AppContext);
	const [selectedIntegration, setSelectedIntegration] = useState<ActionItemIntegration>();
	const [configVisible, setConfigVisible] = useState<boolean>(false);
	const [isEditing, setIsEditing] = useState<boolean>(false);

	const integration = useFragment<ActionItemIntegrationsStreamIntegration$key>(streamIntegrationFragment, data.stream);

	let streamConnectedIntegration: ActionItemIntegration | undefined;
	if (integration?.actionItemIntegration) {
		streamConnectedIntegration = getIntegrationForKey(integration.actionItemIntegration.integrationProvider);
	} else if (integration?.asanaStream) {
		streamConnectedIntegration = getIntegrationForKey("ASANA");
	} else {
		streamConnectedIntegration = undefined;
	}

	const onViewIntegrationConfig = React.useCallback((integration: ActionItemIntegration) => {
		setSelectedIntegration(integration);
		setConfigVisible(true);
	}, []);

	const clearConfigPane = React.useCallback(() => setConfigVisible(false), []);

	const finishConfiguration = React.useCallback(() => {
		if (isEditing) {
			setIsEditing(false);
		}
		setConfigVisible(false);
	}, [isEditing]);

	const changeIntegration = React.useCallback((integration: ActionItemIntegration) => {
		setIsEditing(true);
		setConfigVisible(true);
		setSelectedIntegration(integration);
	}, []);

	const onDisconnect = React.useCallback(() => {
		setIsEditing(false);
	}, []);

	const isPartialUser = stream?.notestreamusers?.find(nsUser => nsUser.user.id === user?.id)?.accessType === "partial";

	const integrationPaneBody = useMemo(() => {
		if (configVisible && selectedIntegration) {
			const ConfigPaneComponent =
				isEditing && integration
					? withIntegrationDetails<IntegrationConfigProps>(IntegrationConfigPane, selectedIntegration, integration)
					: IntegrationConfigPane;

			return (
				<div className={styles.configureIntegration}>
					<ConfigPaneComponent
						integration={selectedIntegration}
						stream={stream}
						viewer={data.viewer}
						onFinish={finishConfiguration}
						onCancel={clearConfigPane}
					/>
				</div>
			);
		} else if (streamConnectedIntegration && integration) {
			return (
				<ConnectedIntegrationDetails
					stream={stream}
					viewer={data.viewer}
					streamConnectedIntegration={streamConnectedIntegration}
					onClickChange={changeIntegration}
					integrationData={integration}
					onDisconnect={onDisconnect}
				/>
			);
		} else {
			return (
				<>
					<div className={styles.sectionTitle}>Integrations</div>
					<div className={styles.sectionDescription}>
						Automatically send action items into your task management system of choice.
					</div>
					<hr className={styles.sectionDivider} />
					<div className={styles.subsectionTitle}>Choose an integration to get started:</div>
					{getIntegrationStreamSettingsCards().map((Card, index) => (
						<Card
							// eslint-disable-next-line react/no-array-index-key
							key={index}
							viewer={data.viewer}
							onClickIntegration={onViewIntegrationConfig}
						/>
					))}
				</>
			);
		}
	}, [
		configVisible,
		selectedIntegration,
		isEditing,
		integration,
		stream,
		data.viewer,
		finishConfiguration,
		clearConfigPane,
		streamConnectedIntegration,
		onDisconnect,
		changeIntegration,
		onViewIntegrationConfig,
	]);

	if (isPartialUser) {
		return (
			<div className={styles.permissionError}>
				<FontAwesomeIcon icon={faExclamationCircle} />
				<p>You do not have permission to modify integration connections on this stream</p>
			</div>
		);
	}

	return <section className={styles.integrationsPane}>{integrationPaneBody}</section>;
}

export interface StreamSettingsIntegrationCardProps {
	integration: ActionItemIntegration;
	onClickIntegration: () => void;
	viewer: ActionItemIntegrationsStreamSettingsModalQuery["response"]["viewer"];
}

export function StreamSettingsIntegrationCard({
	integration,
	onClickIntegration,
	viewer,
}: StreamSettingsIntegrationCardProps) {
	const displayPaywall = React.useCallback(() => {
		showFeatureModal(integration.billingFeatureName);
	}, [integration.billingFeatureName]);

	const activeIntegrations = viewer.connectedActionItemIntegrations;
	const asanaIsConnected = viewer.integrations.asana?.isConnected;

	const isConnected =
		(integration.key === asanaKey && asanaIsConnected) || activeIntegrations.includes(integration.key);

	const showIntegrationOrAuth = React.useCallback(() => {
		if (!isConnected)
			promptConnectingIntegrationModal(integration).then(result => {
				if (result) onClickIntegration();
			});
		else onClickIntegration();
	}, [isConnected, integration, onClickIntegration]);

	return (
		<BillingIdentity viewer={viewer} feature={integration.billingFeatureName}>
			{(extraProps, icon, paywalled) => (
				<button className={styles.integrationCard} onClick={paywalled ? displayPaywall : showIntegrationOrAuth}>
					<div className={styles.cardContentSection}>
						<div className={styles.logo}>{integration.logo}</div>
						{integration.displayName}
					</div>
					<FontAwesomeIcon icon={faChevronRight} color="#6A7980" />
				</button>
			)}
		</BillingIdentity>
	);
}

export interface IntegrationConfigProps<T extends BaseIntegrationDetails = BaseIntegrationDetails> {
	integration: ActionItemIntegration;
	stream: NonNullable<StreamSettingsResponseStream>;
	viewer?: ActionItemIntegrationsStreamSettingsModalQuery["response"]["viewer"];
	onFinish: (integration: ActionItemIntegration) => void;
	onCancel: () => void;
	integrationDetails?: T;
}

function IntegrationConfigPane({
	integration,
	stream,
	viewer,
	onFinish,
	onCancel,
	integrationDetails,
}: IntegrationConfigProps) {
	if (integration?.configurationPane)
		return integration.configurationPane({ integration, stream, viewer, onFinish, onCancel, integrationDetails });
	return <></>;
}

interface ConnectedIntegrationDetailsProps {
	stream: NonNullable<StreamSettingsResponseStream>;
	streamConnectedIntegration: ActionItemIntegration;
	viewer?: ActionItemIntegrationsStreamSettingsModalQuery["response"]["viewer"];
	onClickChange: (integration: ActionItemIntegration) => void;
	integrationData: ActionItemIntegrationsStreamIntegration;
	onDisconnect: () => void;
}

function ConnectedIntegrationDetails({
	stream,
	streamConnectedIntegration,
	onClickChange,
	integrationData,
	onDisconnect,
}: ConnectedIntegrationDetailsProps) {
	const disconnectIntegration = React.useCallback(() => {
		const streamId = stream.id;
		return disconnectStreamIntegration(streamId).then(deletedIntegrationId => {
			if (deletedIntegrationId) {
				toasty.success("Removed integration from stream");
			} else {
				toasty.error("Failed to remove integration from stream");
			}
			onDisconnect();
		});
	}, [stream, onDisconnect]);

	const change = React.useCallback(() => {
		onClickChange(streamConnectedIntegration);
	}, [onClickChange, streamConnectedIntegration]);

	const details = streamConnectedIntegration.useIntegrationDetails(integrationData);

	return (
		<div className={styles.connectedIntegrationDetails}>
			{streamConnectedIntegration.logo}
			<div className={styles.integrationTitle}>{streamConnectedIntegration.displayName}</div>
			<div className={styles.connectionInfoText}>
				Connected to {streamConnectedIntegration.displayName} {streamConnectedIntegration.externalContainerType}{" "}
				<b>{details.containerTitle}</b>
			</div>
			<div className={styles.subtitle}>
				Connected by <b>{details.createdBy}</b>
			</div>
			<div className={styles.buttonRow}>
				<Button size="normal" variant="secondary" onClick={change}>
					Edit
				</Button>
				<Button size="normal" variant="destructive" onClick={disconnectIntegration}>
					Disconnect
				</Button>
			</div>
			<div className={styles.tinyItalicText}>
				Disconnecting the integration from this series will not remove the action item links or delete any data.
			</div>
		</div>
	);
}

export default function ActionItemIntegrationsWrapper({ ...props }: ActionItemIntegrationProps) {
	return (
		<React.Suspense fallback={<LoadingSpinner dimmer={false} inline />}>
			<ActionItemIntegrations {...props} />
		</React.Suspense>
	);
}
